import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Pagination,
  Card,
  CardContent,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { clientAPI, uiAPI } from '../services/api';
import { Client, UiRecord } from '../types';
import StandaloneUiForm from './StandaloneUiForm';
import AppDateField from './AppDateField';
import { getClientDisplayName } from '../utils/clientDisplay';

const PAGE_SIZE = 10;

const formatIncidentDate = (dateString: string | undefined): string => {
  if (!dateString) return 'N/A';
  try {
    const datePart = dateString.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    if (isNaN(year) || isNaN(month) || isNaN(day)) return 'Invalid date';
    return `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}-${year}`;
  } catch {
    return 'Invalid date';
  }
};

const formatIncidentTime = (dateString: string | undefined): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString.includes('T') ? dateString : dateString.replace(' ', 'T'));
    if (isNaN(date.getTime())) return 'Invalid time';
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${hours}:${String(minutes).padStart(2, '0')} ${ampm}`;
  } catch {
    return 'Invalid time';
  }
};

const associationLabel = (record: UiRecord): string => {
  if (record.visit_id) return 'Visit';
  if (record.transportation_id) return 'Transportation';
  return 'Standalone';
};

const UnusualIncidentsList: React.FC = () => {
  const { provider } = useAuth();
  const providerId = provider?.provider_id;

  const [records, setRecords] = useState<UiRecord[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [skip, setSkip] = useState(0);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    clientId: '',
    mui: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRecord, setEditingRecord] = useState<UiRecord | null>(null);

  const fetchRecords = useCallback(async () => {
    if (!providerId) {
      setRecords([]);
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const params: { provider_id: number; client_id?: number; mui?: boolean } = {
        provider_id: providerId,
      };
      if (filters.clientId) params.client_id = Number(filters.clientId);
      if (filters.mui === 'Y') params.mui = true;
      if (filters.mui === 'N') params.mui = false;
      const data = await uiAPI.list(params);
      setRecords(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching unusual incidents:', err);
      setError('Failed to load unusual incidents. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [providerId, filters.clientId, filters.mui]);

  const fetchClients = useCallback(async () => {
    if (!providerId) return;
    try {
      const data = await clientAPI.getClients(providerId);
      setClients(data);
    } catch {
      setClients([]);
    }
  }, [providerId]);

  useEffect(() => {
    fetchRecords();
    fetchClients();
  }, [fetchRecords, fetchClients]);

  useEffect(() => {
    setSkip(0);
  }, [filters]);

  const filteredRecords = useMemo(() => {
    return [...records]
      .filter(r => {
        const day = (r.time_of_incident || '').split('T')[0];
        if (filters.startDate && day && day < filters.startDate) return false;
        if (filters.endDate && day && day > filters.endDate) return false;
        if (filters.startDate && !day) return false;
        return true;
      })
      .sort((a, b) => (b.time_of_incident || '').localeCompare(a.time_of_incident || ''));
  }, [records, filters.startDate, filters.endDate]);

  const total = filteredRecords.length;
  const pageRecords = filteredRecords.slice(skip, skip + PAGE_SIZE);

  const handleCreate = () => {
    setEditingRecord(null);
    setOpenDialog(true);
  };

  const handleEdit = (record: UiRecord) => {
    setEditingRecord(record);
    setOpenDialog(true);
  };

  const handleDelete = async (uiId: number) => {
    if (!window.confirm('Delete this unusual incident record?')) return;
    try {
      await uiAPI.delete(uiId);
      setSuccess('Unusual incident deleted.');
      setTimeout(() => setSuccess(null), 4000);
      fetchRecords();
    } catch (err) {
      console.error('Error deleting unusual incident:', err);
      setError('Failed to delete unusual incident. Please try again.');
    }
  };

  if (isLoading && records.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Unusual Incidents
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          Add Incident
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Filter Incidents
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <AppDateField
                label="Start Date"
                value={filters.startDate || null}
                onChange={v => setFilters(prev => ({ ...prev, startDate: v || '' }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <AppDateField
                label="End Date"
                value={filters.endDate || null}
                onChange={v => setFilters(prev => ({ ...prev, endDate: v || '' }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Client</InputLabel>
                <Select
                  value={filters.clientId}
                  onChange={e => setFilters(prev => ({ ...prev, clientId: e.target.value as string }))}
                  label="Client"
                >
                  <MenuItem value="">
                    <em>All Clients</em>
                  </MenuItem>
                  {clients.map(client => (
                    <MenuItem key={String(client.client_id)} value={String(client.client_id)}>
                      {`${client.first_name} ${client.last_name}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>MUI</InputLabel>
                <Select
                  value={filters.mui}
                  onChange={e => setFilters(prev => ({ ...prev, mui: e.target.value as string }))}
                  label="MUI"
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  <MenuItem value="Y">Yes</MenuItem>
                  <MenuItem value="N">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                onClick={() => setFilters({ startDate: '', endDate: '', clientId: '', mui: '' })}
                sx={{ backgroundColor: '#2D4B73', '&:hover': { backgroundColor: '#1E3452' } }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Client</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>MUI</TableCell>
                <TableCell>Association</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                      No unusual incidents found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pageRecords.map(record => (
                  <TableRow hover key={record.ui_id}>
                    <TableCell>
                      <Typography variant="body2">
                        {getClientDisplayName(clients, record.client_id)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatIncidentDate(record.time_of_incident)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatIncidentTime(record.time_of_incident)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
                        {record.location_of_incident || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={record.mui ? 'Y' : 'N'}
                        color={record.mui ? 'error' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{associationLabel(record)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                        {record.description_of_incident || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleEdit(record)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(record.ui_id)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {total > PAGE_SIZE && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3, mb: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Showing {skip + 1}-{Math.min(skip + PAGE_SIZE, total)} of {total} incidents
          </Typography>
          <Pagination
            count={Math.ceil(total / PAGE_SIZE)}
            page={Math.floor(skip / PAGE_SIZE) + 1}
            onChange={(_, newPage) => setSkip((newPage - 1) * PAGE_SIZE)}
            color="primary"
            showFirstButton
            showLastButton
            size="large"
            variant="outlined"
            shape="rounded"
          />
        </Box>
      )}

      <StandaloneUiForm
        open={openDialog}
        editingRecord={editingRecord}
        onClose={() => {
          setOpenDialog(false);
          setEditingRecord(null);
        }}
        onSaved={() => {
          const wasEdit = !!editingRecord;
          setSuccess(wasEdit ? 'Unusual incident updated successfully.' : 'Unusual incident created successfully.');
          setTimeout(() => setSuccess(null), 4000);
          fetchRecords();
        }}
      />
    </Box>
  );
};

export default UnusualIncidentsList;
